import { Button, Card, CardActions, CardContent, colors, Grid, Modal, TextField, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import clsx from 'clsx';
import { ErrorMessage } from 'components';
import React, { useEffect, useState } from 'react';
import { Inventory, ItemClass } from 'services';
import capitalise from 'utils/capitalise';
import useErrorCatcher from "utils/useErrorCatcher";
import validate from "validate.js";

const useStyles = makeStyles(theme => ({
  root: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    outline: 'none',
    boxShadow: theme.shadows[20],
    width: 450,
    maxHeight: '100%',
    overflowY: 'auto',
    maxWidth: '100%'
  },
  container: {
    marginTop: theme.spacing(3)
  },
  actions: {
    justifyContent: 'flex-end'
  },
  saveButton: {
    color: theme.palette.white,
    backgroundColor: colors.green[600],
    '&:hover': {
      backgroundColor: colors.green[900]
    }
  },
}));

const schema = {
  item_class_id: {
    presence: { allowEmpty: false, message: "is required" },
    numericality: {
      greaterThan: 0
    }
  },
  quantity: {
    presence: { allowEmpty: false, message: "is required" },
    numericality: {
      greaterThan: 0
    }
  },
};

const INIT_VALUES = {
  item_class_id: 0,
  quantity: 1
};
let mounted = false;

const CreateModal = props => {
  let { onCreate, open, onClose, className, inventoryId, ...rest } = props;

  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const runAsyncBlock = useErrorCatcher(error => setErrorMessage(error && capitalise(error.message)));
  const [errorMessage, setErrorMessage] = useState(null);
  const [options, setOptions] = useState([]);

  const [formState, setFormState] = useState({
    isValid: false,
    values: { ...INIT_VALUES },
    touched: {},
    errors: {}
  });

  useEffect(() => {
    runAsyncBlock(async () => {
      const { models } = await ItemClass.list({ query: { limit: 9999 } });
      if (mounted) {
        setOptions(models);
        setFormState({
          isValid: false,
          values: {
            ...INIT_VALUES,
            item_class_id: (models.length && models[0].id) || 0
          },
          touched: {},
          errors: {}
        });
      }
    }).finally(() => mounted && setLoading(false));

  }, [open]); // eslint-disable-line

  useEffect(() => {
    mounted = true;

    return () => { mounted = false; }
  }, []);

  useEffect(() => {
    const errors = validate(formState.values, schema);

    setFormState(formState => ({
      ...formState,
      errors: errors || {}
    }));
  }, [formState.values]);

  if (!open) {
    return null;
  }


  const handleSubmit = event => {
    event.preventDefault();

    setFormState(formState => ({
      ...formState,
      touched: {
        // to show error message
        item_class_id: true,
        quantity: true
      }
    }))
    if (Object.keys(formState.errors).length) return;

    setLoading(true);
    runAsyncBlock(async () => {
      const { item_class_id, quantity } = formState.values;
      let data = { item_class_id, quantity };
      await Inventory.create_item(data, inventoryId);
      onCreate();
      onClose();
    }).finally(() => {
      mounted && setLoading(false);
    });

  }

  const handleFieldChange = event => {
    event.persist();
    setFormState(formState => ({
      ...formState,
      values: {
        ...formState.values,
        [event.target.name]: event.target.type === 'checkbox' ? event.target.checked : event.target.value
      },
      touched: {
        ...formState.touched,
        [event.target.name]: true
      }
    }));
  };

  return (
    <Modal open={open}>
      <Card {...rest} className={clsx(classes.root, className)}>
        <form>
          <CardContent>
            <Typography align="center" gutterBottom variant="h3">
              Create Item
            </Typography>
            <ErrorMessage className={classes.error} message={errorMessage} />
            <Grid className={classes.container} container spacing={3}>
              <Grid item md={12} xs={12}>
                <CustTextField name="item_class_id" required dropdown options={options.map((opt => ({ label: opt.label, value: opt.id })))} label="Item Class" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid>
              {/* <Grid item md={12} xs={12}>
                <CustTextField required type="number" name="quantity" label="Quantity" formState={formState} handleFieldChange={handleFieldChange} />
              </Grid> */}
            </Grid>
          </CardContent>
          <CardActions className={classes.actions}>
            <Button onClick={onClose} variant="contained">Close</Button>
            <Button className={classes.saveButton} onClick={handleSubmit} variant="contained" disabled={loading} >Create</Button>
          </CardActions>
        </form>
      </Card>
    </Modal>
  );
}



const CustTextField = props => {
  let { label, name, formState, handleFieldChange, type, dropdown, options, required } = props;
  const hasError = field => formState.touched[field] && formState.errors[field] ? true : false;

  return (
    <TextField
      fullWidth
      label={label}
      helperText={hasError(name) ? formState.errors[name][0] : null}
      name={name}
      onChange={handleFieldChange}
      value={formState.values[name]}
      error={hasError(name)}
      variant="outlined"
      type={type}
      select={dropdown}
      SelectProps={{ native: true }}
      InputLabelProps={{ shrink: dropdown }}
      required={required}
    >
      {options && options.length > 0 && options.map((opt, index) => (
        <option key={index} value={opt.value}>{opt.label}</option>
      ))
      }
    </TextField>
  );
}


export default CreateModal;